import React from 'react'
import { createContext, useHandler } from 'labguard-cdk'
import { schemeCategory10 } from 'd3'
import uniq from 'lodash/uniq'

/**
 * Context used to manage the current group
 */
export default createContext(
  Provider =>
    function CurvesContext(props) {
      // initialize marks and channels states
      const [marks, setMarks] = React.useState([])
      const [channels, setChannels] = React.useState([])

      // create "add" handlers
      const addMark = useHandler(() =>
        setMarks([...marks, { value: '', unit: '', displayed: true, color: schemeCategory10[marks.length % 10] }])
      )
      const addChannels = useHandler(data =>
        setChannels([
          ...channels,
          ...data.map((channel, index) => ({
            ...channel,
            displayed: true,
            color: schemeCategory10[(channels.length + index) % 10],
          })),
        ])
      )

      // create "update" handlers
      const updateMark = useHandler((mark, updated) =>
        setMarks(marks.map(m => (m === mark ? { ...m, ...updated } : m)))
      )
      const updateChannel = useHandler((channel, updated) =>
        setChannels(channels.map(c => (c === channel ? { ...c, ...updated } : c)))
      )

      // create "delete" handlers
      const deleteMark = useHandler(mark => setMarks(marks.filter(m => m !== mark)))
      const deleteChannel = useHandler(channel => setChannels(channels.filter(c => c !== channel)))

      // remove all marks if no more channels, or remove marks with a unit not present in channels anymore
      React.useEffect(
        () => {
          setMarks(marks.filter(({ unit }) => (!unit && channels.length) || channels.some(c => c.unit === unit)))
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [channels]
      )

      // compute derived data
      const allUnits = React.useMemo(() => uniq(channels.map(({ unit }) => unit)), [channels])
      const visibleUnits = React.useMemo(
        () => uniq(channels.filter(({ displayed }) => displayed).map(({ unit }) => unit)),
        [channels]
      )

      // provide value to Provider component
      return (
        <Provider
          value={{
            marks,
            channels,
            addMark,
            addChannels,
            updateMark,
            updateChannel,
            deleteMark,
            deleteChannel,
            allUnits,
            visibleUnits,
          }}
          {...props}
        />
      )
    }
)
